// Workflow upload helper (2026-10-22 spec §5.1) — the same pipeline the File
// Upload page runs (FileUpload.tsx uploadFile), condensed for the monthly
// close wizard: base64 upload → import-document?channel=… → 202 background
// OCR poll → re-import. Response field names mirror what FileUpload.tsx
// reads against api/src/routes/file-storage.ts:
//   - upload response: `.id` (the new file_records id)
//   - import response: `statement_id` / `statement_ids` / `invoice_id`,
//     `needs_review` (+ `needs_direction_review` / `parse_failed` /
//     `empty_transactions` — FileUpload's needsReview set), `ocr_failed`,
//     `continuity_flags`, `error`
import { api, WORKER_API_BASE } from './api';
import { stageImportMarker } from './importNotices';

export type WorkflowUploadChannel = 'bank_statement' | 'sales_invoice' | 'purchase_invoice';

export interface WorkflowUploadResult {
  file_id: string;
  needs_review: boolean;
  review_id?: string;
  ocr_failed?: boolean;
  continuity_flags?: string[];
  /** True when the 202-poll loop hit its deadline with OCR still `processing`
   *  — no import result exists yet; the caller should say "still processing"
   *  rather than toasting a misleading "Imported". */
  still_processing?: boolean;
}

/** Encrypted-PDF marker (Task 11 amendment): the import endpoint answers
 *  HTTP 200 with {type:'encrypted_pdf', success:false, status:'password_required'}
 *  (api/src/routes/file-storage.ts). The file is ALREADY uploaded at this
 *  point, so the error carries its file_records id — the wizard opens
 *  EncryptedPdfModal against it (try-decrypt decrypts + re-imports
 *  server-side). Cancelling keeps the file stored; it keeps an unlock
 *  button on /file-storage rows (ocr_status 'encrypted'). */
export class WorkflowEncryptedPdfError extends Error {
  readonly fileId: string;
  constructor(fileId: string) {
    super('password_required');
    this.name = 'WorkflowEncryptedPdfError';
    this.fileId = fileId;
  }
}

/** Folder per channel — mirrors CHANNELS in FileUpload.tsx. */
const CHANNEL_FOLDER: Record<WorkflowUploadChannel, string> = {
  bank_statement: 'Bank Statements',
  sales_invoice: 'Invoices',
  purchase_invoice: 'Invoices',
};

/** ocr_status values the 202-poll may treat as settled. Everything else —
 *  pending/processing/importing — keeps polling (see the poll loop below). */
const TERMINAL_OCR_STATUSES = ['completed', 'failed', 'unclear', 'skipped', 'error', 'type_mismatch'];

/** Auth headers for the raw import fetch (Bearer + X-Active-Client), same as
 *  FileUpload.tsx builds around its import-document calls. */
function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const activeClient = localStorage.getItem('activeClient');
  if (activeClient) {
    try {
      const c = JSON.parse(activeClient);
      if (c?.id) headers['X-Active-Client'] = c.id;
    } catch {}
  }
  return headers;
}

async function importDocument(fileId: string, channel: WorkflowUploadChannel): Promise<Response> {
  return fetch(
    `${WORKER_API_BASE}/file-storage/${fileId}/import-document?channel=${encodeURIComponent(channel)}`,
    { method: 'POST', headers: authHeaders(), credentials: 'include' },
  );
}

export async function uploadWorkflowDocument(file: File, channel: WorkflowUploadChannel): Promise<WorkflowUploadResult> {
  // Step 1 — read + store the file. Same JSON body FileUpload.tsx sends
  // (base64 data URL); direct Worker URL bypasses the Pages Function
  // body-size limit.
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
  const up = await api('/file-storage/upload', {
    method: 'POST',
    baseUrl: WORKER_API_BASE,
    body: {
      filename: file.name,
      original_name: file.name,
      file_type: file.type,
      file_size: file.size,
      file_data: base64,
      folder: CHANNEL_FOLDER[channel],
    },
  }) as any;
  const fileId: string = up?.id;
  if (!fileId) throw new Error('upload_failed');

  // Step 2 — OCR + import. Multi-page PDFs are OCR'd in the BACKGROUND queue:
  // the endpoint returns 202; poll the file's ocr_status until terminal, then
  // re-import so the ready text is parsed (GET /file-storage/:id exposes
  // ocr_status at the top level).
  let res = await importDocument(fileId, channel);
  let stillProcessing = false;
  if (res.status === 202) {
    // Stage the durable completion marker FIRST — the banner then owns the
    // finish signal even if this poll gives up at its deadline or the tab
    // closes mid-OCR (2026-10-28 live: 11-min OCR left Step 1 showing nothing).
    stageImportMarker({ fileId, filename: file.name, channel });
    const deadline = Date.now() + 15 * 60 * 1000;
    let terminal = false;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 4000));
      let st: any = null;
      try { st = await api(`/file-storage/${fileId}`); } catch { /* transient — keep polling */ }
      // Terminal statuses ONLY. The lifecycle is pending → processing →
      // importing → completed; breaking on any non-'processing' value re-POSTed
      // the import mid-flight (the importing phase ran ~4 min live) and parsed
      // a bare 202 body as success — "Imported." minutes early.
      if (st?.ocr_status && TERMINAL_OCR_STATUSES.includes(st.ocr_status)) {
        const retry = await importDocument(fileId, channel);
        if (retry.status !== 202) {
          res = retry;
          terminal = true;
          break;
        }
        // A 202 re-import on a terminal file (rare) — keep polling; the
        // deadline bounds the loop.
      }
    }
    // Deadline hit while OCR still runs — `res` is still the bare 202; report
    // still-processing instead of parsing it as an import result.
    stillProcessing = !terminal;
  }

  if (stillProcessing) {
    return { file_id: fileId, needs_review: false, still_processing: true };
  }

  const body: any = await res.json().catch(() => ({}));

  // Encrypted PDF — 200 + {type:'encrypted_pdf', status:'password_required'}.
  // Never parse it as a successful import: throw the typed marker (with the
  // uploaded fileId) so the page can open the password prompt in place.
  if (body?.status === 'password_required' || body?.type === 'encrypted_pdf') {
    throw new WorkflowEncryptedPdfError(fileId);
  }

  // Hard errors (409 duplicate, 4xx/5xx) carry `error` — mirror FileUpload's
  // throw so the caller can toast it.
  if (body?.error && res.status >= 400) throw new Error(body.error);

  return {
    file_id: fileId,
    needs_review: !!(body.needs_review ?? body.needs_direction_review ?? body.company_not_detected ?? body.parse_failed ?? body.empty_transactions),
    review_id: body.statement_id ?? body.statement_ids?.[0] ?? body.invoice_id,
    ocr_failed: !!body.ocr_failed,
    continuity_flags: body.continuity_flags || [],
  };
}
